/* ==========================================================================
   IZEN — CORE BEHAVIOUR
   --------------------------------------------------------------------------
   No dependencies. Everything here is either an IntersectionObserver (free
   when idle) or a pointer handler that writes a CSS variable and lets the
   compositor do the work. Nothing runs a permanent rAF loop.

   Every module is opt-in via a data attribute or class, so this file costs
   nothing on pages that don't use it.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isReduced = () => REDUCED.matches;

  /* ------------------------------------------------------------------
     Reveal — sections and elements entering the viewport.
     Unobserves after firing: a revealed element costs nothing forever after.
     ------------------------------------------------------------------ */
  function initReveal() {
    /* One owner per element. Anything the hero handoff drives is a
       continuous function of scroll position, so it must not also be
       latched on by an observer that fires once and never releases. */
    const targets = Array.from(
      document.querySelectorAll('.izen-reveal, .izen-reveal-bias')
    ).filter((el) => !el.closest('[data-izen-handoff] [data-izen-story]'));
    if (!targets.length) return;

    if (isReduced() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      {
        // threshold MUST stay 0. IntersectionObserver measures the *clipped*
        // rectangle, and .izen-reveal-bias starts clipped to zero width — so
        // any ratio above 0 can never be met, the class is never added, and
        // the element stays invisible forever. Timing is shaped with
        // rootMargin instead, which is unaffected by clipping.
        threshold: 0,
        rootMargin: '0px 0px -10% 0px',
      }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ------------------------------------------------------------------
     Stagger — index children of a .izen-stagger group so their transition
     delays cascade. Done once in JS rather than authored per-item in Liquid.
     ------------------------------------------------------------------ */
  function initStagger() {
    document.querySelectorAll('.izen-stagger').forEach((group) => {
      Array.from(group.children).forEach((child, i) => {
        child.style.setProperty('--izen-i', i);
      });
    });
  }

  /* ------------------------------------------------------------------
     Seam masks — each traced seam needs a document-unique mask id, otherwise
     the first one on the page captures every other instance.
     ------------------------------------------------------------------ */
  let seamId = 0;
  function initSeams(root = document) {
    root.querySelectorAll('.izen-trace').forEach((svg) => {
      if (svg.dataset.izenSeamReady) return;
      const mask = svg.querySelector('mask');
      const stitch = svg.querySelector('.izen-trace__stitch');
      if (!mask || !stitch) return;

      const id = `izen-seam-${(seamId += 1)}`;
      mask.setAttribute('id', id);
      stitch.setAttribute('mask', `url(#${id})`);
      svg.dataset.izenSeamReady = 'true';
    });
  }

  /* ------------------------------------------------------------------
     Header — compacts on scroll and hides on downward scroll past the fold.
     Reads scroll in a rAF-throttled handler; writes only class names.
     ------------------------------------------------------------------ */
  function initHeader() {
    const header = document.querySelector('[data-izen-header]');
    if (!header) return;

    /* Horizon moves the page inside div.page-wrapper from 900px up and clips
       html and body, so window.scrollY never changes there and the header
       neither compacted nor tucked on desktop. Read whatever is actually
       scrolling instead. */
    const scrollTop = () => {
      const de = document.documentElement;
      if (de.scrollHeight > de.clientHeight + 8 &&
          getComputedStyle(de).overflowY !== 'hidden') {
        return de.scrollTop || window.scrollY;
      }
      const wrap = document.querySelector('.page-wrapper');
      return wrap ? wrap.scrollTop : window.scrollY;
    };

    /* The header tucks away while you read downward. That is right for the
       body of the page and wrong for the zip: the sequence is one long
       downward scroll, so the header would leave the moment the garment
       started opening and stay gone for the whole of it. While the stage
       still holds the screen, the header holds its place. */
    const stage = document.querySelector('[data-izen-zip]');
    const stageHasScreen = () => {
      if (!stage) return false;
      const r = stage.getBoundingClientRect();
      return r.bottom > window.innerHeight * 0.5;
    };

    let lastY = scrollTop();
    let ticking = false;

    const update = () => {
      const y = scrollTop();

      header.classList.toggle('is-compact', y > 40);

      const goingDown = y > lastY && y > 240;
      if (!header.contains(document.activeElement)) {
        header.classList.toggle('is-tucked', goingDown && !stageHasScreen());
      }

      lastY = y;
      ticking = false;
    };

    document.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { capture: true, passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------
     Magnetic — an element leans very slightly toward the cursor.
     Writes two CSS variables; the transform itself lives in CSS.
     Pointer-fine only, so touch devices never pay for it.
     ------------------------------------------------------------------ */
  function initMagnetic() {
    if (isReduced()) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    const els = document.querySelectorAll('[data-izen-magnetic]');
    if (!els.length) return;

    els.forEach((el) => {
      const strength = parseFloat(el.dataset.izenMagnetic) || 0.22;
      let frame = null;

      const onMove = (e) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          const dx = (e.clientX - (r.left + r.width / 2)) * strength;
          const dy = (e.clientY - (r.top + r.height / 2)) * strength;
          el.style.setProperty('--izen-mx', `${dx.toFixed(2)}px`);
          el.style.setProperty('--izen-my', `${dy.toFixed(2)}px`);
          frame = null;
        });
      };

      const onLeave = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = null;
        el.style.setProperty('--izen-mx', '0px');
        el.style.setProperty('--izen-my', '0px');
      };

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
    });
  }

  /* ------------------------------------------------------------------
     Seam pressure — the stitch frame compresses slightly under the cursor,
     the way fabric dents when you press it. Publishes the cursor position as
     a percentage; CSS turns that into a gradient hotspot.
     ------------------------------------------------------------------ */
  function initPressure() {
    if (isReduced()) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;

    document.querySelectorAll('[data-izen-pressure]').forEach((el) => {
      let frame = null;

      el.addEventListener('pointermove', (e) => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          const x = ((e.clientX - r.left) / r.width) * 100;
          const y = ((e.clientY - r.top) / r.height) * 100;
          el.style.setProperty('--izen-px', `${x.toFixed(1)}%`);
          el.style.setProperty('--izen-py', `${y.toFixed(1)}%`);
          frame = null;
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     HERITAGE zip — a physical model, not a picture transition.

     The garment is never swapped, cross-faded, or revealed through a shape
     drawn on top of it. The closed master is cut in two along its own zip
     centreline and each half is deformed by a mesh whose displacement is a
     function of how long ago the slider passed that row. The interior is a
     real plate sitting underneath from the first frame; it becomes visible
     only because fabric moved off it.

     Every number is a fraction of the 1254 x 1254 master frame and was
     measured off 01_HERITAGE_CLOSED_MASTER_V2.png, not guessed:
       centreX  mean of 473 sampled zip rows, spread +/- 0.2%
       topY     the tooth contact point beneath the closed slider
       bottomY  where the travelling slider comes to rest: just clear of
                the two-way zip's lower hardware, which is painted into the
                plate and never moves, so the two never stack
       anchorR  chosen so the crescent and star at x 81.7-88.9% are outside
                every displacement ramp, at any progress
       maxOffset  the gap can never grow wider than the real interior on the
                interior plate, so no second set of teeth can appear
  */
  const HZ = {
    zip: { centreX: 0.5127, topY: 0.2710, bottomY: 0.8270 },
    /* The slider is cut from the master itself, so at rest it lands back on
       its own pixels and progress 0 reconstructs the closed garment exactly.
       anchorY is the point on the sprite where the teeth meet it. */
    slider: { width: 0.0330, aspect: 41 / 66, anchorY: 0.9528, centreX: 0.5125 },
    open: {
      maxOffset: 0.058, // per side
      span: 0.34,       // scroll a released row takes to reach full spread
      anchorL: 0.42,    // left panel pinned outboard of this
      anchorR: 0.46,    // right panel pinned outboard of this
      flat: 0.93,       // inner band translates rigidly: teeth never stretch
      hemHold: 0.42,    // the waistband resists
      /* The zip ends at the collar. Above it the garment is one continuous
         hood carrying the neck label at y 15.0-17.9%, so displacement has
         to be exactly zero there: otherwise the label splits into three and
         the hood walks outwards. It reaches full strength by the placket. */
      collarFrom: 0.195,
      collarTo: 0.272,
      /* The anchor above is a purely vertical rule, so it also pinned the one
         thing that has to move first: the zipper-facing collar edge, right
         where the slider starts. That read as the zip catching at the collar.
         This corridor lets that edge — and only that edge — release with the
         slider, while the collar body and the hood stay anchored.
         It stops above the neck label, which sits at y 15.0-17.9%. */
      upperReleaseWidth: 0.86,   // t at which the corridor starts, 1 = centreline
      upperReleaseStartY: 0.185, // inert above this
      upperReleaseEndY: 0.238,   // full strength from here down
      upperReleaseStrength: 1,
      /* Teeth unlock mechanically the moment the slider passes them; the
         cloth then relaxes over u_span. Without the first term the release
         curve left the ground with zero slope and nothing moved for a while. */
      unlockSpan: 0.045,
      unlockAmount: 0.16,
      /* The unlock bonus decays as the cloth relaxes, so it changes the first
         moments after the slider passes a row and leaves the settled geometry
         alone. Without this the whole 25-100% range drifted a few pixels. */
      unlockDecay: 3.0
    },
    /* The slider sprite is split so the pull tab can carry a little weight.
       The two halves share the source alpha complementarily, so at rest they
       recompose the original sprite with zero error. */
    sliderBody: { from: 0, to: 34 / 66 },
    sliderPull: { from: 24 / 66, to: 1 },
    mesh: { cols: 48, rows: 96, mobileCols: 40, mobileRows: 80 },
    scrub: 0.12,
    track: { holdStart: 0.08, holdEnd: 0.9 }
  };

  /* Secondary motion. None of this touches the primary state: every term
     below is multiplied by the same masks the primary displacement already
     uses, so wherever the garment is required to be still — below the
     slider, the hood, the label, the sleeves, the crescent — the secondary
     term is identically zero rather than merely small. */
  const LUX = {
    velocitySmoothing: 0.155,  // first order: ~300 ms settle, no overshoot
    velocityClamp: 0.03,
    fabricLagGain: 1.4,        // scales the released panel's own offset
    fabricLagMax: 0.03,        // never more than 3% of the local displacement
    pullerLagGain: 0.055,      // vertical, frame units per unit velocity
    pullerLagMax: 0.0013,      // about 1.6 px on a 1240 canvas
    pullerSwingGain: 0.75,     // radians per unit velocity
    pullerSwingMax: 0.016,     // about 0.9 degrees
    pullerPivot: 27 / 66,      // where the tab hangs off the slider body
    /* The tab only carries weight once the slider is actually travelling.
       At rest at the top there is no mechanical cause for it to move, and
       the closed state has to stay frozen under any residual velocity. */
    pullerGateTo: 0.05,
    contactShadowFrom: 0.9,    // t at which the contact shadow starts
    contactShadowStrength: 0.16,
    /* The last stretch before the centreline is the tape and the teeth. The
       fabric beside them should fall into shadow; the metal should not, so
       the band is shaped per pixel rather than per vertex — 48 columns
       cannot resolve a feature 2% of a panel wide. */
    contactShadowPeak: 0.972,
    contactShadowRelief: 0.82
  };

  /* Region B — the front collar, and only the front collar.

     The zip holds the two front collar lips together. Once the slider
     leaves the top they lose that tension and fall apart a little: not an
     opening of their own, just the absence of the force that was closing
     them. Without this the garment read as "zip open, collar still shut".

     WHY IT IS NOT THE EXISTING CORRIDOR WIDENED

     The corridor is the zipper-facing edge being released by the slider —
     a mechanical event at the teeth. This is the fabric outboard of that
     edge going slack afterwards. Different cause, different region,
     different response curve, so they are separate terms. Where they
     overlap the new one is scaled by how much release the primary has
     already given the vertex (1 - collar), so the innermost vertices are
     never moved twice.

     EVERY NUMBER MEASURED, NOT CHOSEN

     tFrom  the collar/shoulder junction: at fy 0.195 the collar band on
            the closed master runs from x 0.413 to x 0.493, which is
            t 0.81 to 0.96 — so influence has to be gone by t 0.80.
     yFrom  the IZEN neck patch ends at y 0.1672 (measured: its stitched
            border spans x 0.4906-0.5399, y 0.1427-0.1672). 0.176 leaves
            11 px of the 1254 master between the label and any movement.
     yTo    the hood-opening trim, tracked row by row off the master, runs
            from t 0.785 at y 0.100 to t 0.94 at y 0.215. Below y 0.206
            everything in the t band is the lower opening, not the hood.

     The response saturates: the collar becomes untensioned early and then
     stays untensioned while the panels go on opening. */
  const COLLAR = {
    enabled: true,
    tFrom: 0.80,     // lateral: nothing outboard of the collar/shoulder junction
    tTo: 0.96,       // full at the placket
    yFrom: 0.176,    // nothing at or above: the neck label ends at 0.1672
    yTo: 0.206,      // full through the lower hood opening
    outward: 0.0051, // frame units before masking; 6.4 px on the 1254 master
    down: 0.0014,    // 1.8 px before masking — fabric relaxes, it does not sag
    saturate: 0.55   // of the local release, so most of it happens by p 0.25
  };

  /* The signature layer used to live here: eight constants that read the
     zip's own state and answered with the room's light. Every one of them
     drove a custom property, and every one of those properties was consumed
     by a gradient painted onto a bounded element. Bounded elements have
     corners, corners are straight, and the page showed the box.

     The whole layer is gone rather than retuned, because no value of these
     constants removes the box — only removing the element does. The garment
     is a pure function of (progress, velocity) and always was: none of these
     numbers ever reached a shader uniform. Removing them is why the eleven
     state comparisons in this pass came back at exactly zero. */

  /* Diagnostic isolation only — real-Safari-compositing investigation.
     Read once, from the URL only, wrapped so a hostile or malformed query
     string can never throw. On every load without ?hztest=canvas-empty,
     ?hztest=primitive, ?hztest=body-only or ?hztest=alpha-discard this is
     the empty string, and every branch it gates below is dead code: the
     shader source and the render() call sequence are exactly what they
     were before this constant existed. */
  const HZ_DEBUG = (function () {
    try {
      return new URLSearchParams(window.location.search).get('hztest') || '';
    } catch (e) {
      return '';
    }
  })();

  const HZ_PANEL_VS = [
    'attribute vec2 a_grid;',
    'uniform float u_cx, u_p, u_topY, u_bottomY;',
    'uniform float u_max, u_span, u_anchor, u_flat, u_side, u_hem;',
    'uniform float u_collar0, u_collar1;',
    'uniform float u_relW, u_relY0, u_relY1, u_relStr;',
    'uniform float u_unlockSpan, u_unlockAmt, u_unlockDecay;',
    'uniform float u_lag;',
    'uniform float u_crT0, u_crT1, u_crY0, u_crY1, u_crOut, u_crDown, u_crSat;',
    'varying vec2 v_uv;',
    'varying float v_shade;',
    'varying float v_t;',
    'float ss(float a, float b, float x){',
    '  float t = clamp((x - a) / (b - a), 0.0, 1.0);',
    '  return t * t * (3.0 - 2.0 * t);',
    '}',
    'void main(){',
    /* t runs 0 at the outer silhouette to 1 at the centreline, both sides. */
    '  float t  = a_grid.x;',
    '  float fy = a_grid.y;',
    '  float fx = (u_side < 0.0) ? t * u_cx : 1.0 - t * (1.0 - u_cx);',
    /* The progress at which the slider passes this row. */
    '  float py = clamp((fy - u_topY) / (u_bottomY - u_topY), 0.0, 1.0);',
    /* Nothing below the slider has been released, so nothing below moves. */
    '  float unlock = clamp((u_p - py) / u_unlockSpan, 0.0, 1.0);',
    '  float relax = clamp((u_p - py) / u_span, 0.0, 1.0);',
    '  float settled = relax * relax * (3.0 - 2.0 * relax);',
    '  float open = settled',
    '             + u_unlockAmt * unlock * pow(1.0 - relax, u_unlockDecay);',
    '  float taper = mix(1.0, u_hem, ss(0.32, 0.90, fy));',
    /* Hood, trim and neck label stay put; inside the corridor the
       zipper-facing edge is released anyway. */
    '  float anchor = ss(u_collar0, u_collar1, fy);',
    '  float corridor = ss(u_relW, 1.0, t) * ss(u_relY0, u_relY1, fy) * u_relStr;',
    '  float collar = mix(anchor, 1.0, corridor);',
    /* Zero out at the sleeves, flat over the placket so teeth stay rigid. */
    '  float ramp = ss(u_anchor, u_flat, t);',
    '  float dx = u_max * open * taper * collar * ramp * u_side;',
    /* Secondary: released cloth carries a little mass, so it trails the
       primary position by a fraction of its own offset. Zero wherever dx
       is zero, which is every region that has to stay still. */
    '  dx *= 1.0 - u_lag;',
    /* Region B: the front collar going slack. Bounded secondary geometry
       added on top of the finished primary position, never mixed into it.
       Three independent zeros — outboard of the collar (u_crT0), at and
       above the neck label (u_crY0), and wherever the primary has already
       released the vertex (1 - collar) — so the hood, the back neck, the
       shoulders, the label and every row below the slider are still by
       construction rather than by a small number. Deliberately outside
       the u_lag multiply above: the relaxed shape is a function of how far
       the zip has opened, never of how fast it was scrolled. */
    '  float crLat = ss(u_crT0, u_crT1, t);',
    '  float crHgt = ss(u_crY0, u_crY1, fy);',
    '  float crMask = crLat * crHgt * (1.0 - collar);',
    /* Saturating, with real slope at the origin: smoothstep would leave
       zero with zero slope and put back the dead zone the corridor was
       built to remove. */
    '  float crX = clamp(open / u_crSat, 0.0, 1.0);',
    '  float crAmt = crX * (2.0 - crX);',
    '  dx += u_crOut * crAmt * crMask * u_side;',
    '  float dy = u_crDown * crAmt * crMask;',
    /* Secondary: contact depth where the released edge lifts off the
       interior. It rides the same open*collar mask, so it cannot appear on
       a closed row or on an anchored one. */
    '  v_shade = clamp(open * collar, 0.0, 1.0);',
    '  v_t = t;',
    '  v_uv = vec2(fx, fy);',
    '  gl_Position = vec4((fx + dx) * 2.0 - 1.0, 1.0 - (fy + dy) * 2.0, 0.0, 1.0);',
    '}'
  ].join('\n');

  const HZ_QUAD_VS = [
    'attribute vec2 a_grid;',
    'uniform vec4 u_rect, u_uvRect;',
    'uniform vec4 u_spin;',   // xy pivot, z cos, w sin; identity is 0,0,1,0
    'varying vec2 v_uv;',
    'varying float v_shade;',
    'varying float v_t;',
    'void main(){',
    '  vec2 f = u_rect.xy + a_grid * u_rect.zw;',
    '  vec2 d = f - u_spin.xy;',
    '  f = u_spin.xy + vec2(d.x * u_spin.z - d.y * u_spin.w,',
    '                       d.x * u_spin.w + d.y * u_spin.z);',
    '  v_uv = u_uvRect.xy + a_grid * u_uvRect.zw;',
    '  v_shade = 0.0;',
    '  v_t = 0.0;',
    '  gl_Position = vec4(f.x * 2.0 - 1.0, 1.0 - f.y * 2.0, 0.0, 1.0);',
    '}'
  ].join('\n');

  const HZ_FS_LINES = [
    'precision mediump float;',
    'uniform sampler2D u_tex;',
    'uniform float u_shadow, u_shadeFrom, u_shadePeak, u_shadeRelief;',
    'varying vec2 v_uv;',
    'varying float v_shade;',
    'varying float v_t;',
    'void main(){',
    '  vec4 c = texture2D(u_tex, v_uv);'
  ];
  /* Real-iPhone A/B test (?hztest=alpha-discard vs body-only/production)
     proved this fragment discard is the actual fix for the mobile Safari
     rectangle/blur artifact: a fully-transparent fragment that is written
     with alpha=0 is treated differently by Safari's compositor than one the
     shader refuses to write at all. Threshold (0.001) is the exact value
     tested on-device — kept unchanged. This is the one shared fragment
     shader for both progPanel and progQuad, so the discard applies to every
     draw call through this pipeline (body panels, interior, slider) exactly
     as it did in the proven ?hztest=alpha-discard path. */
  HZ_FS_LINES.push('  if (c.a <= 0.001) { discard; }');
  HZ_FS_LINES.push(
    '  float band = smoothstep(u_shadeFrom, u_shadePeak, v_t);',
    '  band *= 1.0 - u_shadeRelief * smoothstep(u_shadePeak, 1.0, v_t);',
    '  gl_FragColor = vec4(c.rgb * (1.0 - u_shadow * band * v_shade), c.a);',
    '}'
  );
  const HZ_FS = HZ_FS_LINES.join('\n');

  /* Interior-only shading — deliberately its own shader, never merged into
     HZ_FS. Measured on the shipped assets: the interior fill's own colour
     (median rgb 7,6,6) already matches the flat, unshadowed body fabric
     (median rgb 6,6,6) — the source texture is not the problem. What is: the
     panels darken near the gap edge through their own per-vertex contact
     shadow (HZ_PANEL_VS' v_shade, up to LUX.contactShadowStrength), but the
     interior quad has no such term — HZ_QUAD_VS hardcodes v_shade/v_t to 0,
     so through the shared HZ_FS it always renders at full source brightness.

     The flat 0.84 multiplier this replaced was uniform everywhere; the real
     hood cavity in izen-hz-body.png is not. Median luminance sampled in 5
     vertical bands (only near-neutral, alpha=255, non-highlight texels — no
     trim, stitching, label or background), ordered by distance from the
     collar opening (0 = "Uebergang Richtung Kragen", 1 = "oberer innerer
     Kapuzenbereich", the deepest, most occluded point):
       t=0.00  lum=4.00   t=0.25  lum=3.85   t=0.50  lum=3.72
       t=0.75  lum=3.21   t=1.00  lum=2.00
     Brightness stays high near the opening and falls away sharply only in
     the last quarter — a curve fit of norm_lum = 1 - t^gamma against those 5
     points gives gamma = 3.03 (max residual 0.06 of the normalised range;
     plain linear and smoothstep both fit far worse, max residual 0.36/0.45),
     so t^3 is used directly rather than a generic pow(). The measured
     darkest:brightest ratio is 2.00:4.00 = 0.5, applied to the same anchor
     as before (1 - LUX.contactShadowStrength = 0.84, reused rather than a
     new invented number) so the collar end of the interior still matches
     that already-tuned value exactly; the hem end is half of it.

     u_gapTop/u_gapSpan are the exact same by/bh the interior quad's own
     position and UV rect already use (see render()'s bx/by/bw/bh and
     drawInterior() below) — forwarded here read-only so the shader can
     place a fragment within the quad's own vertical extent. No geometry,
     UV rect or draw order changes; HZ_QUAD_VS (shared with progQuad) is
     untouched, and v_uv is the same varying it already outputs. */
  const HZ_INTERIOR_HIGH = 1 - LUX.contactShadowStrength; // collar end, 0.84
  const HZ_INTERIOR_LOW = 0.5 * HZ_INTERIOR_HIGH; // hem end, measured ratio
  const HZ_INTERIOR_FS = [
    'precision mediump float;',
    'uniform sampler2D u_tex;',
    'uniform float u_gapTop, u_gapSpan;',
    'varying vec2 v_uv;',
    'void main(){',
    '  vec4 c = texture2D(u_tex, v_uv);',
    '  if (c.a <= 0.001) { discard; }',
    '  float t = clamp((v_uv.y - u_gapTop) / u_gapSpan, 0.0, 1.0);',
    '  float shade = ' + HZ_INTERIOR_LOW.toFixed(4) + ' + ' + (HZ_INTERIOR_HIGH - HZ_INTERIOR_LOW).toFixed(4) + ' * (1.0 - t * t * t);',
    '  gl_FragColor = vec4(c.rgb * shade, c.a);',
    '}'
  ].join('\n');

  function initZipper() {
    const roots = Array.from(document.querySelectorAll('[data-izen-hz]'));
    if (!roots.length) return;

    const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
    const span = (v, a, b) => clamp01((v - a) / (b - a));
    const lerp = (a, b, t) => a + (b - a) * t;
    const smooth = (t) => t * t * (3 - 2 * t);

    /* Reduced motion keeps the zipper — it is the user's own scroll driving
       a deterministic state, not an animation playing at them. What it drops
       is the secondary polish: inertia, puller weight, settling. */
    const quiet = () => REDUCED.matches;

    roots.forEach((root) => {
      if (root.dataset.izenHzBound === '1') return;
      root.dataset.izenHzBound = '1';

      const canvas = root.querySelector('[data-izen-hz-canvas]');
      const imgBody = root.querySelector('[data-izen-hz-body]');
      const imgInterior = root.querySelector('[data-izen-hz-interior]');
      const imgSliderBody = root.querySelector('[data-izen-hz-slider-body]');
      const imgSliderPull = root.querySelector('[data-izen-hz-slider-pull]');
      if (!canvas || !imgBody || !imgInterior || !imgSliderBody || !imgSliderPull) return;

      const gl =
        canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false }) ||
        canvas.getContext('experimental-webgl', { alpha: true, antialias: true });
      if (!gl) return;

      const track = root.closest('[data-izen-zip]') || root.parentElement;

      /* ---------------- programs ---------------- */
      function compile(type, src) {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
          // eslint-disable-next-line no-console
          console.warn('[IZEN] zip shader:', gl.getShaderInfoLog(s));
          return null;
        }
        return s;
      }

      function link(vsSrc, fsSrc) {
        const vs = compile(gl.VERTEX_SHADER, vsSrc);
        const fs = compile(gl.FRAGMENT_SHADER, fsSrc || HZ_FS);
        if (!vs || !fs) return null;
        const p = gl.createProgram();
        gl.attachShader(p, vs);
        gl.attachShader(p, fs);
        gl.bindAttribLocation(p, 0, 'a_grid');
        gl.linkProgram(p);
        if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
          // eslint-disable-next-line no-console
          console.warn('[IZEN] zip program:', gl.getProgramInfoLog(p));
          return null;
        }
        return p;
      }

      const progPanel = link(HZ_PANEL_VS);
      const progQuad = link(HZ_QUAD_VS);
      /* Same vertex shader as progQuad (interior geometry/UV are unchanged),
         a different, isolated fragment shader — see HZ_INTERIOR_FS above.
         progPanel and progQuad, and everything they draw (body panels,
         slider, the ?hztest=primitive patch), still compile the exact same
         HZ_FS they always have. */
      const progInterior = link(HZ_QUAD_VS, HZ_INTERIOR_FS);
      if (!progPanel || !progQuad || !progInterior) return;

      const uPanel = {};
      ['u_cx', 'u_p', 'u_topY', 'u_bottomY', 'u_max', 'u_span', 'u_anchor', 'u_flat', 'u_side', 'u_hem', 'u_collar0', 'u_collar1', 'u_relW', 'u_relY0', 'u_relY1', 'u_relStr', 'u_unlockSpan', 'u_unlockAmt', 'u_unlockDecay', 'u_lag', 'u_crT0', 'u_crT1', 'u_crY0', 'u_crY1', 'u_crOut', 'u_crDown', 'u_crSat', 'u_shadeFrom', 'u_shadePeak', 'u_shadeRelief', 'u_shadow', 'u_tex'].forEach(
        (n) => {
          uPanel[n] = gl.getUniformLocation(progPanel, n);
        }
      );
      const uQuad = {
        u_rect: gl.getUniformLocation(progQuad, 'u_rect'),
        u_uvRect: gl.getUniformLocation(progQuad, 'u_uvRect'),
        u_spin: gl.getUniformLocation(progQuad, 'u_spin'),
        u_tex: gl.getUniformLocation(progQuad, 'u_tex')
      };
      const uInterior = {
        u_rect: gl.getUniformLocation(progInterior, 'u_rect'),
        u_uvRect: gl.getUniformLocation(progInterior, 'u_uvRect'),
        u_spin: gl.getUniformLocation(progInterior, 'u_spin'),
        u_tex: gl.getUniformLocation(progInterior, 'u_tex'),
        u_gapTop: gl.getUniformLocation(progInterior, 'u_gapTop'),
        u_gapSpan: gl.getUniformLocation(progInterior, 'u_gapSpan')
      };

      /* ---------------- geometry ---------------- */
      const small = window.matchMedia('(max-width: 899px)').matches;
      const cols = small ? HZ.mesh.mobileCols : HZ.mesh.cols;
      const rows = small ? HZ.mesh.mobileRows : HZ.mesh.rows;

      const grid = new Float32Array(cols * rows * 12);
      let gi = 0;
      for (let c = 0; c < cols; c += 1) {
        const t0 = c / cols;
        const t1 = (c + 1) / cols;
        for (let r = 0; r < rows; r += 1) {
          const y0 = r / rows;
          const y1 = (r + 1) / rows;
          grid[gi] = t0; grid[gi + 1] = y0;
          grid[gi + 2] = t1; grid[gi + 3] = y0;
          grid[gi + 4] = t0; grid[gi + 5] = y1;
          grid[gi + 6] = t1; grid[gi + 7] = y0;
          grid[gi + 8] = t1; grid[gi + 9] = y1;
          grid[gi + 10] = t0; grid[gi + 11] = y1;
          gi += 12;
        }
      }
      const gridBuf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, gridBuf);
      gl.bufferData(gl.ARRAY_BUFFER, grid, gl.STATIC_DRAW);
      const gridCount = cols * rows * 6;

      const quadBuf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 0, 1, 1, 0, 1]), gl.STATIC_DRAW);

      /* ---------------- textures ---------------- */
      function upload(img) {
        const t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        /* Straight alpha the whole way: PNG stores it unpremultiplied, this
           keeps it so, the shader writes it so, blendFuncSeparate composites
           it so, and the context is premultipliedAlpha:false, so the browser
           premultiplies exactly once, at the end. One convention, no double
           multiply anywhere. */
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        /* No mip chain. With the backing buffer at full device resolution the
           plates minify by 1.0-1.33x, which LINEAR handles; measured against a
           supersampled reference a mip chain removed 14% of the weave detail
           and improved the silhouette by 0.7 of a grey level. */
        return t;
      }

      let texBody = null;
      let texInterior = null;
      let texBody2 = null;
      let texPull = null;

      /* ---------------- painting ---------------- */
      let target = 0;
      let shown = 0;
      let lastP = 0;
      let vel = 0;
      let frame = null;
      let live = false;

      /* ---------------- the zip's progress, published once ----------------

         The desktop hero composes the wordmark over the garment's chest and
         glides it up as the zip runs. That glide must not be a second
         reading of the scroll: two readings of the same scroll can still
         disagree — different easings, different frames, different rounding —
         and the mark would drift off the slider. So the renderer hands out
         the one number it is painting with, on the frame it paints it, and
         CSS does the rest.

         Two properties leave here, both derived from that single p:

           --izen-hz-p      the raw zip progress, 0..1
           --izen-hero-lift the desktop text's own progress, 0..1

         The second exists because the shaping belongs in one place. CSS had
         been doing it, as clamp(0, p * 4.2, 1) — a straight line that hit 1
         at p = 0.238, so the mark had finished its whole journey inside the
         first quarter of the zip and then simply sat there. The curve below
         replaces it: nothing happens for the first twentieth, the middle is
         a smoothstep so the mark leaves and arrives without a corner, and it
         does not finish until 0.78. CSS now reads a number that is already
         shaped rather than shaping one itself.

         Above 900px only. Below it both properties are removed rather than
         left stale, so the phone's DOM carries nothing it did not carry
         before and no mobile rule can ever read them. */
      const LIFT = {
        /* A beat of stillness before anything moves: the slider has to be
           seen to start before the mark reacts to it. */
        from: 0.05,
        /* And it is gone well before the garment finishes opening, so the
           last stretch of the zip is the piece alone. */
        to: 0.78
      };
      const WIDE = window.matchMedia('(min-width: 900px)');
      let published = null;
      let curP = 0;

      function publish(p) {
        if (!track) return;
        if (!WIDE.matches) {
          if (published !== null) {
            track.style.removeProperty('--izen-hz-p');
            track.style.removeProperty('--izen-hero-lift');
            delete track.dataset.izenHeroLift;
            published = null;
          }
          return;
        }

        const n = clamp01((p - LIFT.from) / (LIFT.to - LIFT.from));
        const lift = n * n * (3 - 2 * n);

        /* Four decimals is finer than a pixel of travel at any viewport, and
           skipping the identical write keeps the style recalc off frames
           where nothing moved. */
        const v = `${p.toFixed(4)} ${lift.toFixed(4)}`;
        if (v === published) return;
        published = v;
        track.style.setProperty('--izen-hz-p', p.toFixed(4));
        track.style.setProperty('--izen-hero-lift', lift.toFixed(4));
        /* Set only from in here, which is only ever reached once the
           renderer is live. A browser without WebGL never gets it, and its
           copy keeps the ordinary scrolling behaviour rather than holding
           on a garment that is never going to open. */
        if (track.dataset.izenHeroLift !== '') track.dataset.izenHeroLift = '';
      }

      /* ---------------- the composition's own geometry ----------------

         The mark starts under the hem, and the hem is where the frame
         element actually is — not where a stack of viewport units says it
         should be. CSS could only re-derive it from the same terms that
         produce it, and that derivation goes stale the moment any of them
         moves; the element already knows.

         Two numbers leave here, both in px, both on the stage:

           --izen-hero-floor  the frame's bottom edge, relative to the copy
           --izen-hero-text   the text block's own measured height

         The second is what the media reserves at its bottom edge and what
         the travel has to clear. CSS declares an estimate of it on the same
         element, so this inline write simply wins once it lands and the
         layout is never without a value.

         Measured on load and on resize only — a ResizeObserver on the two
         elements whose size can change, plus the breakpoint itself. Never
         on scroll, never per frame. Writing --izen-hero-text invalidates
         layout and reading the rects below forces it back once, which is
         the entire cost, once per resize.

         There is no feedback loop to guard against: the text's height
         follows the piece, the piece follows the viewport, and neither
         follows the reserve this writes. */
      const copyEl = track ? track.querySelector('.izen-hero__copy') : null;
      const footEl = copyEl ? copyEl.querySelector('.izen-hero__foot') : null;
      const frameEl = root.querySelector('.izen-hz__frame');
      let geoWritten = false;

      let lastFrameW = 0;

      function clearGeometry() {
        if (!geoWritten || !track) return;
        track.style.removeProperty('--izen-hero-offset');
        track.style.removeProperty('--izen-hero-floor');
        track.style.removeProperty('--izen-hero-text');
        geoWritten = false;
        lastFrameW = 0;
      }

      function measureGeometry() {
        if (!track || !copyEl || !footEl || !frameEl) return;
        if (!WIDE.matches) {
          clearGeometry();
          return;
        }

        /* How far below the top of the screen the stage begins. It is the
           state the reader opens the page in, and the composition has to
           fit inside what is left of the screen there — not inside the
           100svh it gets later, once the pin has carried it to the top.
           Read with the scroll added back, so it is the same number at any
           scroll position and nothing here has to watch the scroll. */
        const sc = document.querySelector('.page-wrapper');
        const scrolled =
          sc && sc.scrollHeight > sc.clientHeight + 8
            ? sc.scrollTop
            : window.scrollY || document.documentElement.scrollTop || 0;
        const offset = Math.max(0, Math.round(track.getBoundingClientRect().top + scrolled));
        track.style.setProperty('--izen-hero-offset', `${offset}px`);

        /* Reading a layout value here settles the write above. */
        const textH = footEl.offsetHeight;
        if (!textH) return;
        track.style.setProperty('--izen-hero-text', `${textH.toFixed(1)}px`);

        const f = frameEl.getBoundingClientRect();
        const c = copyEl.getBoundingClientRect();
        if (!f.height || !c.height) return;
        track.style.setProperty('--izen-hero-floor', `${(f.bottom - c.top).toFixed(1)}px`);
        geoWritten = true;

        /* The offset feeds the piece's height cap, so the frame itself can
           change size here — and the drawing buffer is sized off that frame.
           A ResizeObserver pass is not a window resize, so the renderer has
           to be told. It settles in one further pass: the offset does not
           depend on the piece, so the second measurement writes the same
           number and the frame stops changing. */
        if (Math.abs(f.width - lastFrameW) > 0.5) {
          lastFrameW = f.width;
          resize();
          schedule();
        }
      }

      let geoFrame = null;
      function scheduleGeometry() {
        if (geoFrame) return;
        geoFrame = requestAnimationFrame(() => {
          geoFrame = null;
          measureGeometry();
        });
      }

      if (window.ResizeObserver && footEl && frameEl) {
        const ro = new ResizeObserver(scheduleGeometry);
        ro.observe(footEl);
        ro.observe(frameEl);
      } else {
        window.addEventListener('resize', scheduleGeometry, { passive: true });
      }

      const onWideChange = () => {
        publish(curP);
        /* Below the breakpoint this removes both geometry properties; above
           it, it measures the layout the new breakpoint just produced. */
        scheduleGeometry();
      };
      if (WIDE.addEventListener) WIDE.addEventListener('change', onWideChange);
      else if (WIDE.addListener) WIDE.addListener(onWideChange);

      function resize() {
        const box = root.querySelector('.izen-hz__frame');
        if (!box) return;
        const r = box.getBoundingClientRect();
        /* A phone reports 3. Capping at 2 meant a 671 px buffer stretched by
           CSS across 1005 device pixels: the buffer, not the screen, was the
           limit, and the plates minified 1.9x into it. The silhouette was
           being resolved well below what the display could show, which is
           what made the edge read as crunchy there and not on a desktop. At 3
           the buffer matches the screen and the plates minify 1.25x, which
           plain LINEAR handles without a mip chain. */
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        /* The canvas must occupy a WHOLE number of device pixels, or the
           compositor rescales the finished frame and every texel of the
           garment picks up a bilinear tap.

           The frame is width: min(86vw, ...), so on a phone its CSS width is
           fractional: 335.3906 at 390. Three times that is 1006.17 device
           pixels, and a backing store can only be an integer, so the layer is
           presented at a scale of 1.00017 rather than 1. Measured on the
           deployed site, WebKit, DPR 3: the drawing buffer read back straight
           off the GPU scores 56.3 on a Laplacian of the chest; the same area
           photographed off the screen scores 41.2. A quarter of the detail the
           GPU produced never reached the glass, and only on a phone — desktop
           lands on the 620px cap, where 620x1 and 620x2 are already exact and
           buffer and screenshot are bit-identical.

           Rounding the canvas to whole CSS pixels makes css x dpr an integer
           at dpr 1, 2 and 3, so the scale is exactly 1 and the compositor has
           nothing to resample. The leftover fraction goes into left/top so the
           garment stays centred; measured, the silhouette moves at most 0.25
           CSS px and its area changes by 0.33%. max-width has to go with it:
           base.css caps canvas at 100%, which would clamp a rounded-up width
           straight back to the fractional one. */
        const cw = Math.max(1, Math.round(r.width));
        const ch = Math.max(1, Math.round(r.height));
        const lx = (r.width - cw) / 2;
        const ly = (r.height - ch) / 2;
        if (canvas.style.maxWidth !== 'none') canvas.style.maxWidth = 'none';
        if (canvas.style.width !== cw + 'px') canvas.style.width = cw + 'px';
        if (canvas.style.height !== ch + 'px') canvas.style.height = ch + 'px';
        if (canvas.style.left !== lx + 'px') canvas.style.left = lx + 'px';
        if (canvas.style.top !== ly + 'px') canvas.style.top = ly + 'px';
        const w = Math.max(1, Math.round(cw * dpr));
        const h = Math.max(1, Math.round(ch * dpr));
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
      }

      function bindGrid(prog, buf) {
        gl.useProgram(prog);
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
      }

      function drawQuad(tex, x, y, w, h, uv, spin) {
        bindGrid(progQuad, quadBuf);
        gl.uniform4f(uQuad.u_rect, x, y, w, h);
        if (spin) gl.uniform4f(uQuad.u_spin, spin[0], spin[1], spin[2], spin[3]);
        else gl.uniform4f(uQuad.u_spin, 0, 0, 1, 0);
        if (uv) gl.uniform4f(uQuad.u_uvRect, uv[0], uv[1], uv[2], uv[3]);
        else gl.uniform4f(uQuad.u_uvRect, 0, 0, 1, 1);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1i(uQuad.u_tex, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }

      /* Same quad geometry/UV path as drawQuad, bound to progInterior
         instead of progQuad so only the interior's own draw call picks up
         HZ_INTERIOR_FS's gradient. No spin — the interior never rotates.
         y and h ARE by/bh from render()'s own bx/by/bw/bh — the caller
         already passes that box as both the position rect and the UV rect,
         so no new value is computed here, just forwarded to the shader as
         the vertical span it shades across. */
      function drawInterior(tex, x, y, w, h, uv) {
        bindGrid(progInterior, quadBuf);
        gl.uniform4f(uInterior.u_rect, x, y, w, h);
        gl.uniform4f(uInterior.u_spin, 0, 0, 1, 0);
        gl.uniform4f(uInterior.u_uvRect, uv[0], uv[1], uv[2], uv[3]);
        gl.uniform1f(uInterior.u_gapTop, y);
        gl.uniform1f(uInterior.u_gapSpan, h);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.uniform1i(uInterior.u_tex, 0);
        gl.drawArrays(gl.TRIANGLES, 0, 6);
      }

      function drawPanel(side, p, lag) {
        bindGrid(progPanel, gridBuf);
        gl.uniform1f(uPanel.u_cx, HZ.zip.centreX);
        gl.uniform1f(uPanel.u_p, p);
        gl.uniform1f(uPanel.u_topY, HZ.zip.topY);
        gl.uniform1f(uPanel.u_bottomY, HZ.zip.bottomY);
        gl.uniform1f(uPanel.u_max, HZ.open.maxOffset);
        gl.uniform1f(uPanel.u_span, HZ.open.span);
        gl.uniform1f(uPanel.u_anchor, side < 0 ? HZ.open.anchorL : HZ.open.anchorR);
        gl.uniform1f(uPanel.u_flat, HZ.open.flat);
        gl.uniform1f(uPanel.u_side, side);
        gl.uniform1f(uPanel.u_hem, HZ.open.hemHold);
        gl.uniform1f(uPanel.u_collar0, HZ.open.collarFrom);
        gl.uniform1f(uPanel.u_collar1, HZ.open.collarTo);
        gl.uniform1f(uPanel.u_relW, HZ.open.upperReleaseWidth);
        gl.uniform1f(uPanel.u_relY0, HZ.open.upperReleaseStartY);
        gl.uniform1f(uPanel.u_relY1, HZ.open.upperReleaseEndY);
        gl.uniform1f(uPanel.u_relStr, HZ.open.upperReleaseStrength);
        gl.uniform1f(uPanel.u_unlockSpan, HZ.open.unlockSpan);
        gl.uniform1f(uPanel.u_unlockAmt, HZ.open.unlockAmount);
        gl.uniform1f(uPanel.u_unlockDecay, HZ.open.unlockDecay);
        gl.uniform1f(uPanel.u_lag, lag);
        /* Disabling is done here, by sending zero displacement, so the
           shader keeps exactly one code path. */
        gl.uniform1f(uPanel.u_crT0, COLLAR.tFrom);
        gl.uniform1f(uPanel.u_crT1, COLLAR.tTo);
        gl.uniform1f(uPanel.u_crY0, COLLAR.yFrom);
        gl.uniform1f(uPanel.u_crY1, COLLAR.yTo);
        gl.uniform1f(uPanel.u_crOut, COLLAR.enabled ? COLLAR.outward : 0);
        gl.uniform1f(uPanel.u_crDown, COLLAR.enabled ? COLLAR.down : 0);
        gl.uniform1f(uPanel.u_crSat, COLLAR.saturate);
        gl.uniform1f(uPanel.u_shadeFrom, LUX.contactShadowFrom);
        gl.uniform1f(uPanel.u_shadePeak, LUX.contactShadowPeak);
        gl.uniform1f(uPanel.u_shadeRelief, LUX.contactShadowRelief);
        gl.uniform1f(uPanel.u_shadow, LUX.contactShadowStrength);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, texBody);
        gl.uniform1i(uPanel.u_tex, 0);
        gl.drawArrays(gl.TRIANGLES, 0, gridCount);
      }

      function render(p, vel) {
        const v = vel || 0;
        const clampTo = (x, m) => (x > m ? m : x < -m ? -m : x);
        const lag = clampTo(v * LUX.fabricLagGain, LUX.fabricLagMax);

        resize();
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        /* ?hztest=canvas-empty only: clear, then stop. No draw call of any
           kind reaches this framebuffer. Every other load falls straight
           through, exactly as before this line existed. */
        if (HZ_DEBUG === 'canvas-empty') return;

        gl.disable(gl.BLEND);

        /* ?hztest=primitive only: skip every real draw call and paint one
           small quad sampled from a patch of texBody already proven fully
           opaque (alpha 255 across the whole 0.45-0.55 UV box), so the rest
           of the canvas has nothing on it but the clear above. */
        if (HZ_DEBUG === 'primitive') {
          drawQuad(texBody, 0.4, 0.4, 0.2, 0.2, [0.45, 0.45, 0.05, 0.05]);
          return;
        }

        /* The interior is only ever seen through the gap, so paint just the
           band the gap can reach. Everything else would be overdrawn by the
           panels anyway, and this is most of the fill cost on a phone. */
        const bx = HZ.zip.centreX - HZ.open.maxOffset - 0.03;
        const bw = 2 * (HZ.open.maxOffset + 0.03);
        const by = HZ.open.collarFrom - 0.03;
        const bh = HZ.zip.bottomY + 0.04 - by;
        /* ?hztest=body-only only: the interior draw call is skipped. */
        if (HZ_DEBUG !== 'body-only') {
          drawInterior(texInterior, bx, by, bw, bh, [bx, by, bw, bh]);
        }
        drawPanel(-1, p, lag);
        drawPanel(1, p, lag);

        /* ?hztest=body-only only: stop before the slider draws too. */
        if (HZ_DEBUG === 'body-only') return;

        gl.enable(gl.BLEND);
        /* Separate alpha. blendFunc alone computes dst.a = src.a^2 +
           dst.a*(1-src.a), which dips below 1 wherever the slider's soft edge
           sits on opaque cloth — invisible while the canvas was opaque, a
           see-through halo now that it is not. ONE/ONE_MINUS_SRC_ALPHA is the
           correct straight-alpha "over" for the alpha channel. */
        gl.blendFuncSeparate(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA,
                             gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

        const contact = lerp(HZ.zip.topY, HZ.zip.bottomY, p);
        const sw = HZ.slider.width;
        const sh = sw / HZ.slider.aspect;
        const sx = HZ.slider.centreX - sw / 2;
        const top = contact - sh * HZ.slider.anchorY;

        /* The tab hangs off the body, so it trails a hair when the slider is
           driven and settles back when it stops. The body itself never
           moves off the validated path. */
        const gate = smooth(span(p, 0, LUX.pullerGateTo));
        const drop = clampTo(v * LUX.pullerLagGain, LUX.pullerLagMax) * gate;
        const swing = clampTo(v * LUX.pullerSwingGain, LUX.pullerSwingMax) * gate;
        const pivotY = top + sh * LUX.pullerPivot;
        const spin = [HZ.slider.centreX, pivotY, Math.cos(swing), Math.sin(swing)];

        const pf = HZ.sliderPull.from;
        drawQuad(
          texPull,
          sx, top + sh * pf - drop, sw, sh * (HZ.sliderPull.to - pf),
          null, spin
        );
        drawQuad(texBody2, sx, top, sw, sh * HZ.sliderBody.to);
      }

      function paint() {
        frame = null;
        if (!live) return;

        const gap = target - shown;
        if (Math.abs(gap) > 0.0004) shown += gap * HZ.scrub;
        else shown = target;

        const p = smooth(span(shown, HZ.track.holdStart, HZ.track.holdEnd));

        /* Secondary state. First order, so it settles without overshoot and
           yields to input on the next frame. It never feeds back into p. */
        const raw = p - lastP;
        lastP = p;
        vel += (raw - vel) * LUX.velocitySmoothing;
        if (vel > LUX.velocityClamp) vel = LUX.velocityClamp;
        else if (vel < -LUX.velocityClamp) vel = -LUX.velocityClamp;
        if (quiet()) vel = 0;

        render(p, vel);
        /* Same p, same frame as the garment it was just drawn with. */
        curP = p;
        publish(p);

        /* Keep painting while the scroll is catching up or the cloth is
           still settling. The third condition here used to be the room's
           arrival ease, which held the loop open for roughly 400ms after
           the garment had already stopped — redrawing identical pixels.
           With no room to arrive, those frames have nothing left to do. */
        if (Math.abs(gap) > 0.0004 || Math.abs(vel) > 0.00002) {
          frame = requestAnimationFrame(paint);
        }
      }

      function measure() {
        if (!track) return 0;
        const stage = track.firstElementChild;
        const travel = track.offsetHeight - (stage ? stage.offsetHeight : 0);
        if (travel <= 0) return 0;
        return clamp01(-track.getBoundingClientRect().top / travel);
      }

      function schedule() {
        target = measure();
        if (!frame) frame = requestAnimationFrame(paint);
      }

      /* ---------------- start ---------------- */
      const sources = [imgBody, imgInterior, imgSliderBody, imgSliderPull];
      let ready = 0;
      const arm = () => {
        ready += 1;
        if (ready < sources.length) return;
        texBody = upload(imgBody);
        texInterior = upload(imgInterior);
        texBody2 = upload(imgSliderBody);
        texPull = upload(imgSliderPull);
        live = true;
        root.classList.add('is-live');
        /* Before the first paint: publish() sets the attribute the pin is
           gated on, so the copy must already know where the hem is. */
        measureGeometry();
        schedule();
      };
      const fail = () => {
        // eslint-disable-next-line no-console
        console.warn('[IZEN] Heritage zip: an asset failed — the closed garment stays.');
      };
      sources.forEach((el) => {
        if (el.complete && el.naturalWidth) arm();
        else {
          el.addEventListener('load', arm, { once: true });
          el.addEventListener('error', fail, { once: true });
        }
      });

      /* Horizon scrolls div.page-wrapper from 900px up and clips html and
         body, so the window never sees a scroll event there. Capture on
         document catches whichever element is actually moving. */
      document.addEventListener('scroll', schedule, { capture: true, passive: true });
      const onResize = () => {
        resize();
        schedule();
      };
      window.addEventListener('resize', onResize, { passive: true });
      window.addEventListener('orientationchange', onResize, { passive: true });

      /* A hook for the automated quality tests: it takes the animation's
         own progress and paints that one frame. No overlay, no readout,
         nothing a customer can reach or see. */
      root.izenRenderAt = (v, secondary) => {
        if (!live) return null;
        const p = clamp01(v);
        render(p, secondary || 0);
        curP = p;
        publish(p);
        return p;
      };
    });
  }

  /* ------------------------------------------------------------------
     Hero -> Story handoff.

     The garment is finished when it is open. What is left is the question
     of how the page stops being about the garment, and the answer here is
     composition rather than another effect: the hero holds for the last
     stretch of its own track, then leaves the way any section leaves, and
     the story arrives into the space it vacates.

     THE HANDOFF COSTS NO SCROLL

     Both terms of the distance are already in the layout. The stage's
     track ends with a hold — the zip reaches 1 at HZ.track.holdEnd and the
     remaining tenth is the breathing region, the garment standing open
     with the hero still owning the screen. After that the hero is simply
     scrolling away, over exactly its own height. So the handoff is a
     reading of movement the page was going to make anyway; it adds no
     distance, holds nothing, and intercepts nothing.

     SEPARATE FROM THE ZIP

     handoff is its own value. It does not read the zip's progress and the
     zip never reads it; both are pure functions of the same scroll
     position, which is why reverse is exact in both without a single line
     about reversing. Two groups are shaped from it — the contextual line,
     then the section itself — and nothing else moves.

     WHY IT TAKES THE STORY OFF THE GENERIC REVEAL

     .izen-reveal is an IntersectionObserver that fires once, unobserves,
     and never comes back. Scrolling up through the boundary would leave
     the story fully composed above a garment that is closing again. Under
     the handoff the section's state is the scroll position, so going back
     up genuinely undoes it.
     ------------------------------------------------------------------ */
  const HANDOFF = {
    /* How far the story takes to take over, in hero heights. The hero is
       leaving over that distance regardless. A phone gets less of it: the
       premium is precision, not duration. */
    emergence: 1,
    mobileEmergence: 0.72,
    mobileTo: 899,
    /* Two ramps, deliberately overlapping so there is no stage boundary to
       see. Both start after the breathing region, which the geometry puts
       at 0.163 on a phone and 0.167 on a desktop — the same composition at
       both sizes, without a second set of numbers. */
    leadFrom: 0.16,
    leadTo: 0.44,
    bodyFrom: 0.32,
    bodyTo: 0.74,
    /* The contextual line is never fully absent: a label at a sixth of its
       weight reads as part of the room rather than as something waiting to
       be faded in. */
    leadFloor: 0.16
  };

  function initHandoff() {
    const tracks = Array.from(document.querySelectorAll('[data-izen-zip]'));
    if (!tracks.length) return;

    const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
    const span = (v, a, b) => clamp01((v - a) / (b - a));
    const smooth = (t) => t * t * (3 - 2 * t);

    tracks.forEach((track) => {
      if (track.dataset.izenHandoffBound === '1') return;

      const home = track.closest('.izen-home') || track.parentElement;
      const story = home ? home.querySelector('[data-izen-story]') : null;
      /* No story after the stage means there is nothing to hand off to.
         The section keeps the ordinary reveal and this module stays out. */
      if (!home || !story) return;
      track.dataset.izenHandoffBound = '1';

      let start = 0;
      let dist = 0;
      let frame = null;

      /* Measured on load, on resize and on orientation change only. The
         scroll path reads one rectangle and writes three variables. */
      function metrics() {
        const hero = track.firstElementChild;
        const heroH = hero ? hero.offsetHeight : 0;
        const travel = track.offsetHeight - heroH;
        if (travel <= 0 || heroH <= 0) {
          start = 0;
          dist = 0;
          return;
        }
        const wide = window.innerWidth > HANDOFF.mobileTo;
        start = travel * HZ.track.holdEnd;
        dist =
          travel * (1 - HZ.track.holdEnd) +
          heroH * (wide ? HANDOFF.emergence : HANDOFF.mobileEmergence);
      }

      function write(forced) {
        frame = null;
        let h = 0;
        if (typeof forced === 'number') {
          h = clamp01(forced);
        } else if (dist > 0) {
          h = clamp01((-track.getBoundingClientRect().top - start) / dist);
        }

        /* Reduced motion keeps the zip and drops the choreography: the
           story is simply present, exactly as .izen-reveal already
           behaves under the preference. */
        const hush = isReduced();
        const lead = hush ? 1 : smooth(span(h, HANDOFF.leadFrom, HANDOFF.leadTo));
        const body = hush ? 1 : smooth(span(h, HANDOFF.bodyFrom, HANDOFF.bodyTo));

        const st = home.style;
        st.setProperty('--izen-handoff', h.toFixed(4));
        st.setProperty('--izen-story-lead', lead.toFixed(4));
        st.setProperty('--izen-story-body', body.toFixed(4));
        return h;
      }

      /* rAF takes a timestamp, and a timestamp is a number: passing write
         straight to it would read the clock as a forced value. */
      function schedule() {
        if (!frame) frame = requestAnimationFrame(() => write());
      }

      metrics();
      write();
      /* Only now: the attribute is what tells the stylesheet the handoff
         owns this section, so it must never be set before there are values
         for it to own. */
      home.dataset.izenHandoff = '';

      document.addEventListener('scroll', schedule, { capture: true, passive: true });
      const onResize = () => {
        metrics();
        schedule();
      };
      window.addEventListener('resize', onResize, { passive: true });
      window.addEventListener('orientationchange', onResize, { passive: true });

      /* Quality tests only: paints one handoff position. No visuals of its
         own, nothing a customer can reach. */
      track.izenHandoffAt = (v) => write(clamp01(v));
      track.izenHandoffSpan = () => ({ start: start, dist: dist });
    });
  }

  /* ------------------------------------------------------------------
     Logo — sews the IZEN wordmark stitch by stitch.

     Uses each path's real length rather than a normalised one, so every
     letter is sewn at the same stitch rate: a wide letter genuinely takes
     longer than a narrow one, exactly like a machine.
     ------------------------------------------------------------------ */
  function sewLogo(svg, { rate = 260, gap = 90, onDone } = {}) {
    const paths = svg.querySelectorAll('.izen-logo__mask-path');
    if (!paths.length) {
      if (onDone) onDone();
      return;
    }

    if (isReduced()) {
      paths.forEach((p) => {
        p.style.transition = 'none';
        p.style.strokeDashoffset = '0';
      });
      if (onDone) onDone();
      return;
    }

    let elapsed = 0;

    paths.forEach((path) => {
      const len = path.getTotalLength();
      const duration = (len / rate) * 1000;

      path.style.strokeDasharray = `${len}`;
      path.style.strokeDashoffset = `${len}`;
      path.style.transition = `stroke-dashoffset ${duration}ms linear ${elapsed}ms`;

      // Next tick, so the browser registers the starting offset first.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          path.style.strokeDashoffset = '0';
        });
      });

      elapsed += duration + gap;
    });

    if (onDone) setTimeout(onDone, elapsed);
    return elapsed;
  }

  function initLogo() {
    document.querySelectorAll('[data-izen-logo]').forEach((svg) => {
      // Skip the loader's copy — initLoader drives that one so it can dismiss
      // the overlay when the sewing finishes.
      if (svg.hasAttribute('data-izen-loader-logo')) return;

      // Rates are in path units per second. A letter outline runs roughly
      // 380 units, so this sews each letter in about a quarter second —
      // present, but never something the visitor waits on.
      sewLogo(svg, { rate: 1500, gap: 30 });
    });
  }

  /* ------------------------------------------------------------------
     Loader — first paint only.

     Deliberately does not hold the page hostage: it resolves on whichever
     comes first, the sewing finishing or a hard ceiling. Repeat visits in
     the same session skip it entirely.
     ------------------------------------------------------------------ */
  function initLoader() {
    const loader = document.querySelector('[data-izen-loader]');
    if (!loader) return;

    const dismiss = () => {
      if (loader.dataset.izenDone) return;
      loader.dataset.izenDone = 'true';
      loader.classList.add('is-open');
      document.documentElement.classList.remove('izen-loading');
      // Remove from the a11y tree and the paint path once it has opened.
      setTimeout(() => loader.remove(), 900);
    };

    let seen = false;
    try {
      seen = sessionStorage.getItem('izen-seen') === '1';
    } catch (e) {
      /* private mode — treat as first visit */
    }

    if (seen || isReduced()) {
      loader.remove();
      document.documentElement.classList.remove('izen-loading');
      return;
    }

    try {
      sessionStorage.setItem('izen-seen', '1');
    } catch (e) {
      /* ignore */
    }

    // Slower than the header's copy — this is the one moment the mark is
    // allowed to take its time.
    const svg = loader.querySelector('[data-izen-loader-logo]');
    if (svg) sewLogo(svg, { rate: 900, gap: 60, onDone: dismiss });

    // Hard ceiling: the loader never blocks longer than this, whatever happens.
    setTimeout(dismiss, 2600);
  }

  /* ------------------------------------------------------------------
     Mobile menu — full-screen overlay.

     Traps nothing exotic: it just moves focus in, restores it on close, and
     closes on Escape. `hidden` is removed a frame before the open class so
     the parting transition actually runs.
     ------------------------------------------------------------------ */
  function initMenu() {
    const menu = document.querySelector('[data-izen-menu]');
    const toggle = document.querySelector('[data-izen-menu-toggle]');
    if (!menu || !toggle) return;

    const closeBtn = menu.querySelector('[data-izen-menu-close]');
    let lastFocus = null;

    const open = () => {
      lastFocus = document.activeElement;
      menu.hidden = false;
      requestAnimationFrame(() => menu.classList.add('is-open'));
      toggle.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    };

    const close = () => {
      menu.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      const done = () => {
        menu.hidden = true;
      };
      if (isReduced()) done();
      else setTimeout(done, 560);
      if (lastFocus) lastFocus.focus();
    };

    toggle.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Following a link should close the overlay, including same-page anchors
    // where no navigation event fires.
    menu.querySelectorAll('a').forEach((a) => a.addEventListener('click', close));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) close();
    });
  }

  /* ------------------------------------------------------------------
     Withdrawal form — native dialog, three small steps, no dependency.
     The final screen mirrors the entered values before Shopify's contact
     form sends the declaration to the shop inbox.
     ------------------------------------------------------------------ */
  function initWithdrawal(root = document) {
    const dialog = root.querySelector?.('[data-izen-withdrawal-dialog]') ||
      document.querySelector('[data-izen-withdrawal-dialog]');
    if (!dialog || dialog.dataset.izenWithdrawalReady) return;
    dialog.dataset.izenWithdrawalReady = 'true';

    const form = dialog.querySelector('form');
    const openers = document.querySelectorAll('[data-izen-withdrawal-open]');
    const closeButtons = dialog.querySelectorAll('[data-izen-withdrawal-close]');
    let step = 1;

    const show = (nextStep) => {
      step = nextStep;
      dialog.querySelectorAll('[data-izen-step]').forEach((panel) => {
        const active = Number(panel.dataset.izenStep) === step;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });
      dialog.querySelectorAll('[data-izen-step-indicator]').forEach((item) => {
        item.classList.toggle('is-active', Number(item.dataset.izenStepIndicator) === step);
      });
      dialog.querySelector(`[data-izen-step="${step}"] h3`)?.focus({ preventScroll: true });
    };

    const validateStep = () => {
      const panel = dialog.querySelector(`[data-izen-step="${step}"]`);
      const fields = panel ? panel.querySelectorAll('input, textarea, select') : [];
      for (const field of fields) {
        if (!field.reportValidity()) return false;
      }
      return true;
    };

    const renderSummary = () => {
      const summary = dialog.querySelector('[data-izen-withdrawal-summary]');
      if (!summary || !form) return;
      const rows = [
        ['Name', form.elements['contact[name]']?.value || 'Nicht angegeben'],
        ['Bestellnummer', form.elements['contact[Bestellnummer]']?.value],
        ['E-Mail', form.elements['contact[email]']?.value],
        ['Artikel', form.elements['contact[Artikel]']?.value],
        ['Datum', form.elements['contact[Bestell- oder Erhaltsdatum]']?.value || 'Nicht angegeben'],
      ];
      summary.replaceChildren();
      rows.forEach(([label, value]) => {
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = label;
        detail.textContent = value || '—';
        summary.append(term, detail);
      });
    };

    openers.forEach((button) => button.addEventListener('click', () => {
      show(1);
      dialog.showModal();
    }));
    closeButtons.forEach((button) => button.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.querySelectorAll('[data-izen-withdrawal-next]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!validateStep()) return;
        if (step === 2) renderSummary();
        show(Math.min(3, step + 1));
      });
    });
    dialog.querySelectorAll('[data-izen-withdrawal-back]').forEach((button) => {
      button.addEventListener('click', () => show(Math.max(1, step - 1)));
    });

    if (dialog.querySelector('.izen-withdrawal__success, .izen-withdrawal__errors')) {
      dialog.showModal();
    }
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */
  function boot() {
    initSeams();
    initStagger();
    /* Before initReveal: the handoff claims its section by setting the
       attribute the reveal filters on. */
    initHandoff();
    initReveal();
    initHeader();
    initMagnetic();
    initPressure();
    initZipper();
    initLogo();
    initMenu();
    initLoader();
    initWithdrawal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Shopify's theme editor re-renders sections in place; re-bind into them.
  document.addEventListener('shopify:section:load', (e) => {
    initSeams(e.target);
    initStagger();
    initHandoff();
    initReveal();
    initMagnetic();
    initPressure();
    initZipper();
    initLogo();
    initWithdrawal(e.target);
  });

  // Expose the few things other IZEN modules need.
  window.IZEN = { sewLogo, initSeams, initReveal };
})();
